import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import api from "../api";

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 18px;
`;

const FormContainer = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  align-items: end;
`;

const InputArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 6px;
  height: 38px;
  padding: 0 10px;
  font-size: 14px;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 6px;
  height: 38px;
  padding: 0 10px;
  font-size: 14px;
  background: #fff;
`;

const CheckboxWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #444;
  height: 38px;
`;

const Button = styled.button`
  border: none;
  border-radius: 6px;
  height: 38px;
  background: #2980b9;
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #1a6a9a;
  }

  &:disabled {
    background: #9eb6c7;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px 0;
  color: #1a5276;
  font-size: 18px;
`;

const TableWrap = styled.div`
  margin-top: 16px;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f0f6fb;
  color: #1a5276;
  border-bottom: 1px solid #d9e8f3;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #eee;
`;

const Empty = styled.div`
  margin-top: 12px;
  color: #777;
  font-size: 14px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${(p) => (p.ativo ? "#e6f6ec" : "#f6e6e6")};
  color: ${(p) => (p.ativo ? "#1e8449" : "#922b21")};
`;

const FormLigas = ({ user }) => {
  const [ligas, setLigas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const [nome, setNome] = useState("");
  const [ano, setAno] = useState(new Date().getFullYear());
  const [tipoCompeticao, setTipoCompeticao] = useState(1);
  const [ativo, setAtivo] = useState(true);

  const tipoTexto = (id) => {
    const tipo = tipos.find((t) => Number(t.id) === Number(id));
    if (tipo) return tipo.nome;
    if (Number(id) === 2) return "CopaDoMundo";
    return "Brasileirao";
  };

  const carregarLigas = async () => {
    try {
      const res = await api.get("liga/listarLigas");
      setLigas(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setLigas([]);
      toast.error("Nao foi possivel carregar ligas.");
    }
  };

  const carregarTipos = async () => {
    try {
      const res = await api.get("liga/tiposCompeticao");
      const lista = Array.isArray(res.data) ? res.data : [];
      setTipos(lista);
      if (lista.length > 0) {
        setTipoCompeticao(Number(lista[0].id));
      }
    } catch {
      setTipos([
        { id: 1, nome: "Brasileirao" },
        { id: 2, nome: "CopaDoMundo" },
      ]);
      setTipoCompeticao(1);
    }
  };

  useEffect(() => {
    carregarTipos();
    carregarLigas();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!nome.trim()) {
      toast.warn("Informe o nome da liga.");
      return;
    }

    if (!ano || Number(ano) < 2000) {
      toast.warn("Informe um ano valido.");
      return;
    }

    if (!user?.id) {
      toast.error("Usuario invalido para criar liga.");
      return;
    }

    const payload = {
      nome: nome.trim(),
      idUsuario: Number(user.id),
      ativo,
      ano: Number(ano),
      tipoCompeticao: Number(tipoCompeticao),
    };

    try {
      setSalvando(true);
      await api.post("liga/addLiga", payload);
      toast.success("Liga criada com sucesso!");
      setNome("");
      setAno(new Date().getFullYear());
      setTipoCompeticao(1);
      setAtivo(true);
      carregarLigas();
    } catch (error) {
      const msg = error?.response?.data?.message || "Erro ao criar liga.";
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Card>
      <SectionTitle>Cadastro de Ligas</SectionTitle>

      <FormContainer onSubmit={handleSubmit}>
        <InputArea>
          <Label>Nome da Liga *</Label>
          <Input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Copa do Mundo"
          />
        </InputArea>

        <InputArea>
          <Label>Ano *</Label>
          <Input
            type="number"
            min="2000"
            value={ano}
            onChange={(e) => setAno(e.target.value)}
          />
        </InputArea>

        <InputArea>
          <Label>Tipo de Competicao *</Label>
          <Select
            value={tipoCompeticao}
            onChange={(e) => setTipoCompeticao(Number(e.target.value))}
          >
            {tipos.map((item) => (
              <option key={item.id} value={item.id}>
                {item.nome}
              </option>
            ))}
          </Select>
        </InputArea>

        <InputArea>
          <Label>Status</Label>
          <CheckboxWrap>
            <input
              type="checkbox"
              checked={ativo}
              onChange={(e) => setAtivo(e.target.checked)}
            />
            Ativa
          </CheckboxWrap>
        </InputArea>

        <Button type="submit" disabled={salvando}>
          {salvando ? "Salvando..." : "Criar Liga"}
        </Button>
      </FormContainer>

      {ligas.length === 0 ? (
        <Empty>Nenhuma liga cadastrada.</Empty>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Nome</Th>
                <Th>Ano</Th>
                <Th>Tipo</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {ligas.map((liga) => (
                <tr key={liga.id}>
                  <Td>{liga.nome}</Td>
                  <Td>{liga.ano}</Td>
                  <Td>{tipoTexto(liga.tipoCompeticao)}</Td>
                  <Td>
                    <Badge ativo={liga.ativo}>{liga.ativo ? "Ativa" : "Inativa"}</Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Card>
  );
};

export default FormLigas;
